import { CheckCircle2, Copy, XCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { toast } from "@/hooks/use-toast"
interface ParsedHttp {
  method: string
  url: string
  statusCode: string
  headers: { key: string; value: string }[]
  body: string
}
interface HttpViewerProps {
  title: string
  content: string
  statusCode?: string
}
const parseHttpContent = (content: string): ParsedHttp => {
  const result: ParsedHttp = {
    method: "",
    url: "",
    statusCode: "",
    headers: [],
    body: "",
  }
  if (!content) return result
  const jsonMatch = content.match(/(\{[\s\S]*\})/)
  if (jsonMatch) {
    result.body = jsonMatch[1]
  }
  const lines = content.split("\n")
  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed.startsWith("REQUEST --->")) {
      const match = trimmed.match(
        /REQUEST ---> (\w+) (.+?) HTTP/
      )
      if (match) {
        result.method = match[1]
        result.url = match[2]
      }
      continue
    }
    if (trimmed.startsWith("RESPONSE <---")) {
      const match = trimmed.match(/HTTP\/[\d.]+ (\d+)/)
      if (match) {
        result.statusCode = match[1]
      }
      continue
    }
    if (
      trimmed.includes(":") &&
      !trimmed.startsWith("{") &&
      !trimmed.startsWith("--")
    ) {
      if (
        trimmed.startsWith("REQUEST") ||
        trimmed.startsWith("RESPONSE")
      )
        continue
      const colonIndex = trimmed.indexOf(":")
      if (colonIndex > 0) {
        const key = trimmed.substring(0, colonIndex).trim()
        const value = trimmed
          .substring(colonIndex + 1)
          .trim()
        if (
          key &&
          value &&
          !key.includes(" ") &&
          key.length < 50
        ) {
          result.headers.push({ key, value })
        }
      }
    }
  }
  return result
}
const formatJson = (text: string): string => {
  if (!text) return ""
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}
export const HttpViewer = ({
  title,
  content,
  statusCode,
}: HttpViewerProps) => {
  const { t } = useTranslation()
  const parsed = parseHttpContent(content)
  const isSuccess = statusCode?.startsWith("2")
  const isError =
    statusCode?.startsWith("4") ||
    statusCode?.startsWith("5")
  const copyBody = () => {
    navigator.clipboard.writeText(parsed.body || content)
    toast({
      title: t("files.contentCopied"),
      variant: "success",
    })
  }
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between bg-muted/50 px-4 py-3">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium">
            {title}
          </CardTitle>
          {parsed.method && (
            <Badge
              variant="outline"
              className="font-mono text-xs"
            >
              {parsed.method}
            </Badge>
          )}
          {statusCode && (
            <Badge
              variant={
                isSuccess
                  ? "default"
                  : isError
                    ? "destructive"
                    : "secondary"
              }
              className="gap-1 font-mono text-xs"
            >
              {isSuccess && (
                <CheckCircle2 className="h-3 w-3" />
              )}
              {isError && <XCircle className="h-3 w-3" />}
              {statusCode}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={copyBody}
          title="Copiar body"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="divide-y divide-border p-0">
        {parsed.url && (
          <div className="p-3">
            <p className="mb-1 text-xs text-muted-foreground">
              URL
            </p>
            <p className="break-all font-mono text-sm">
              {parsed.url}
            </p>
          </div>
        )}
        {parsed.headers.length > 0 && (
          <div className="p-3">
            <p className="mb-2 text-xs text-muted-foreground">
              Headers
            </p>
            <div className="space-y-1">
              {parsed.headers.map((header, idx) => (
                <div
                  key={idx}
                  className="flex font-mono text-xs"
                >
                  <span className="shrink-0 text-muted-foreground">
                    {header.key}:
                  </span>
                  <span className="ml-2 break-all">
                    {header.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {parsed.body && (
          <div className="p-3">
            <p className="mb-2 text-xs text-muted-foreground">
              Body
            </p>
            <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs">
              {formatJson(parsed.body)}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
